import { User, Session } from "@supabase/supabase-js";
import supabase from "../lib/supabaseClient";
import apiService from "./apiService";
import {
  OAUTH_CONFIG,
  OAuthProviderName,
  getOAuthProvider,
  getOAuthScopes,
  hasRequiredScopes,
  getMissingScopes,
} from "../config/oauthConfig";
import { API_URL, FRONTEND_URL } from "env-config";

export interface UserProviderInfo {
  provider: OAuthProviderName | "email" | "unknown";
  email: string;
  hasRequiredScopes: boolean;
  connectedProviders: string[];
  needsReconnection: boolean;
  missingScopes: string[];
}

export interface OAuthIntegration {
  id: string;
  provider: string;
  status: string;
  scopes: string[];
  expires_at?: string;
  created_at: string;
}

export class OAuthService {
  /**
   * Detect which OAuth provider the user used to sign in
   */
  static detectUserProvider(
    user: User | null
  ): OAuthProviderName | "email" | "unknown" {
    if (!user) return "unknown";

    const provider = user.app_metadata?.provider;
    const providers = user.app_metadata?.providers;

    // Check providers array first (for users with multiple auth methods)
    if (Array.isArray(providers) && providers.length > 0) {
      if (providers.includes("google")) return "google";
      if (providers.includes("azure")) return "microsoft";
    }

    // Check single provider
    if (provider === "google") return "google";
    if (provider === "azure") return "microsoft";
    if (provider === "email") return "email";

    // Fallback: check email domain patterns for better UX
    if (user.email) {
      if (
        user.email.endsWith("@gmail.com") ||
        user.email.endsWith("@googlemail.com")
      ) {
        return "google";
      }
      if (
        user.email.endsWith("@outlook.com") ||
        user.email.endsWith("@hotmail.com") ||
        user.email.endsWith("@live.com")
      ) {
        return "microsoft";
      }
    }

    return "unknown";
  }

  /**
   * Get comprehensive provider information for the user
   */
  static async getUserProviderInfo(
    user: User | null
  ): Promise<UserProviderInfo> {
    if (!user) {
      return {
        provider: "unknown",
        email: "",
        hasRequiredScopes: false,
        connectedProviders: [],
        needsReconnection: false,
        missingScopes: [],
      };
    }

    const detectedProvider = this.detectUserProvider(user);

    try {
      // Get OAuth integrations from backend
      const integrations = await this.getUserIntegrations();
      const connectedProviders = integrations.map((i) => i.provider);

      // For OAuth providers, check scopes
      if (detectedProvider === "google" || detectedProvider === "microsoft") {
        const providerIntegration = integrations.find(
          (i) => i.provider === detectedProvider
        );

        if (providerIntegration) {
          const userHasRequiredScopes = hasRequiredScopes(
            providerIntegration.scopes || [],
            detectedProvider
          );

          const missingScopes = getMissingScopes(
            providerIntegration.scopes || [],
            detectedProvider
          );

          return {
            provider: detectedProvider,
            email: user.email || "",
            hasRequiredScopes: userHasRequiredScopes,
            connectedProviders,
            needsReconnection: !userHasRequiredScopes,
            missingScopes,
          };
        } else {
          // No integration found - needs connection
          return {
            provider: detectedProvider,
            email: user.email || "",
            hasRequiredScopes: false,
            connectedProviders,
            needsReconnection: true,
            missingScopes: getOAuthScopes(detectedProvider),
          };
        }
      }

      // For email or unknown providers
      return {
        provider: detectedProvider,
        email: user.email || "",
        hasRequiredScopes: false,
        connectedProviders,
        needsReconnection:
          detectedProvider !== "email" && detectedProvider !== "unknown",
        missingScopes: [],
      };
    } catch (error) {
      console.error("Failed to get provider info:", error);

      // Return safe fallback
      return {
        provider: detectedProvider,
        email: user.email || "",
        hasRequiredScopes: false,
        connectedProviders: [],
        needsReconnection:
          detectedProvider !== "email" && detectedProvider !== "unknown",
        missingScopes: [],
      };
    }
  }

  /**
   * Sign in with OAuth provider using correct scopes
   */
  static async signInWithProvider(provider: OAuthProviderName): Promise<void> {
    const config = getOAuthProvider(provider);
    const scopes = getOAuthScopes(provider);

    // Redirect to API first, which will process tokens and redirect to frontend
    const redirectTo = `${API_URL}/auth/v1/callback`;

    // Always redirect to auth callback first, which will determine the final destination
    const { error } = await supabase.auth.signInWithOAuth({
      provider: config.supabaseProvider as any,
      options: {
        redirectTo,
        scopes: scopes.join(" "),
        queryParams: {
          access_type: "offline", // For Google refresh tokens
          // Always show account chooser and request consent to ensure scopes
          prompt: "select_account consent",
        },
      },
    });

    if (error) {
      console.error(`OAuth error for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Get user's OAuth integrations from backend
   */
  static async getUserIntegrations(): Promise<OAuthIntegration[]> {
    try {
      const response = await apiService.get("/oauth/integrations");
      return response.data.integrations || [];
    } catch (error) {
      console.error("Failed to get integrations:", error);
      return [];
    }
  }

  /**
   * Check if user needs OAuth connection for core functionality
   */
  static async needsOAuthConnection(user: User): Promise<boolean> {
    const providerInfo = await this.getUserProviderInfo(user);
    return providerInfo.needsReconnection;
  }

  /**
   * Validate OAuth permissions for specific functionality
   */
  static async validatePermissions(user: User): Promise<{
    canSendEmails: boolean;
    canReadContacts: boolean;
    provider: string;
    issues: string[];
  }> {
    const result = {
      canSendEmails: false,
      canReadContacts: false,
      provider: "unknown",
      issues: [] as string[],
    };

    try {
      const providerInfo = await this.getUserProviderInfo(user);
      result.provider = providerInfo.provider;

      if (providerInfo.provider === "email") {
        result.issues.push(
          "Email provider does not support OAuth integrations"
        );
        return result;
      }

      if (providerInfo.provider === "unknown") {
        result.issues.push("Unable to detect OAuth provider");
        return result;
      }

      if (!providerInfo.hasRequiredScopes) {
        result.issues.push(
          `Missing required permissions: ${providerInfo.missingScopes.join(
            ", "
          )}`
        );
        return result;
      }

      // If we have required scopes, we can send emails and read contacts
      result.canSendEmails = true;
      result.canReadContacts = true;
    } catch (error) {
      console.error("Failed to validate permissions:", error);
      result.issues.push("Failed to validate permissions");
    }

    return result;
  }

  /**
   * Get display name for provider
   */
  static getProviderDisplayName(provider: string): string {
    if (provider === "google") return OAUTH_CONFIG.providers.google.displayName;
    if (provider === "microsoft")
      return OAUTH_CONFIG.providers.microsoft.displayName;
    if (provider === "email") return "Email";
    return "Unknown";
  }

  /**
   * Initialize OAuth integration after sign-in (simplified)
   */
  static async initializeOAuthIntegration(user: User): Promise<void> {
    try {
      const provider = this.detectUserProvider(user);

      // Only handle actual OAuth providers
      if (provider !== "google" && provider !== "microsoft") {
        return;
      }

      // The backend will handle token capture automatically when OAuth callback happens
      // We don't need to create placeholder integrations anymore
      console.log(
        `OAuth integration initialization for ${provider} - backend will handle token capture`
      );
    } catch (error) {
      console.error("Failed to initialize OAuth integration:", error);
      // Don't throw - this shouldn't block the UI
    }
  }

  /**
   * Capture OAuth tokens from Supabase session and store them in backend
   */
  static async captureOAuthTokensFromSession(
    session: Session
  ): Promise<boolean> {
    if (!session?.user || !session.provider_token) {
      console.log("No OAuth tokens in session to capture");
      return false;
    }

    const user = session.user;
    const detectedProvider = this.detectUserProvider(user);

    // Only process actual OAuth providers
    if (detectedProvider !== "google" && detectedProvider !== "microsoft") {
      console.log(`Not an OAuth provider: ${detectedProvider}`);
      return false;
    }

    try {
      console.log(`Capturing ${detectedProvider} tokens from session`);

      // Extract tokens from Supabase session
      const tokens = {
        provider: detectedProvider,
        access_token: session.provider_token,
        refresh_token: session.provider_refresh_token || null,
        expires_in: session.expires_in || 3600,
        scope: this.getSessionScopes(session, detectedProvider),
      };

      console.log("Extracted tokens:", {
        provider: tokens.provider,
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in,
        scope: tokens.scope,
      });

      // Send tokens to our backend for storage
      const response = await apiService.post("/oauth/callback", tokens);

      if (response.data.success) {
        console.log(
          `Successfully stored ${detectedProvider} integration:`,
          response.data.integration
        );
        return true;
      } else {
        console.error("Backend failed to store tokens:", response.data);
        return false;
      }
    } catch (error) {
      console.error(`Failed to capture ${detectedProvider} tokens:`, error);
      return false;
    }
  }

  /**
   * Extract scopes from Supabase session
   */
  private static getSessionScopes(
    session: Session,
    provider: OAuthProviderName
  ): string {
    // Try to get scopes from session metadata
    if (session.user?.app_metadata?.provider_scopes) {
      return session.user.app_metadata.provider_scopes.join(" ");
    }

    // Fallback to default scopes for the provider
    const defaultScopes = getOAuthScopes(provider);
    return defaultScopes.join(" ");
  }
}
